/**
 * Render tipográfico Flyer V2 — layouts estructurales distintos.
 * forwardRef para captura html2canvas (export PNG/JPG/PDF).
 */
import React from 'react';
import { getFlyerFormat } from './flyerFormats';
import { getFlyerTemplate } from './flyerTemplates';

function LogoBlock({ brand, maxH = 56, ink = '#fff' }) {
  if (brand.showLogo && brand.logo) {
    return (
      <img
        src={brand.logo}
        alt=""
        style={{
          maxHeight: maxH,
          maxWidth: 200,
          objectFit: 'contain',
          display: 'block'
        }}
      />
    );
  }
  return (
    <div
      style={{
        fontWeight: 800,
        fontSize: 22,
        letterSpacing: 0.4,
        color: ink
      }}
    >
      {brand.nombre}
    </div>
  );
}

function ContactFooter({ brand, compact }) {
  if (!brand.showContact && !brand.showQr) return null;
  const bits = [];
  if (brand.showContact) {
    if (brand.whatsapp) bits.push(brand.whatsapp);
    if (brand.web) bits.push(brand.web);
    if (brand.direccion && !compact) bits.push(brand.direccion);
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        marginTop: 'auto',
        paddingTop: compact ? 12 : 20
      }}
    >
      <div style={{ fontSize: compact ? 14 : 16, opacity: 0.85, lineHeight: 1.35, minWidth: 0 }}>
        {bits.map((b) => (
          <div key={b}>{b}</div>
        ))}
      </div>
      {brand.showQr && brand.qrImageUrl ? (
        <img
          src={brand.qrImageUrl}
          alt=""
          style={{
            width: compact ? 64 : 88,
            height: compact ? 64 : 88,
            borderRadius: 8,
            background: '#fff',
            padding: 4,
            flexShrink: 0
          }}
        />
      ) : null}
    </div>
  );
}

function PriceBlock({ subject, promo, showPrice, showCompare, showPromo, accent, huge, ink = '#fff' }) {
  if (!showPrice) return null;
  const promoPrice = showPromo && promo?.precioFinalFmt ? promo.precioFinalFmt : null;
  const main = promoPrice || subject?.price?.formatted;
  if (!main) {
    return (
      <div style={{ fontSize: 18, opacity: 0.7, fontWeight: 600, color: ink }}>Consultar precio</div>
    );
  }
  const compare =
    showCompare && !promoPrice && subject?.compare?.showCompare
      ? subject.compare.precioAnteriorFmt
      : showPromo && promo?.precioBaseFmt && promoPrice
        ? promo.precioBaseFmt
        : null;
  const badge = showPromo && promo?.badge ? promo.badge : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {badge ? (
        <span
          style={{
            alignSelf: 'flex-start',
            background: accent,
            color: '#111',
            fontWeight: 800,
            fontSize: huge ? 18 : 14,
            padding: '6px 12px',
            borderRadius: 999
          }}
        >
          {badge}
        </span>
      ) : null}
      {compare ? (
        <div
          style={{
            fontSize: huge ? 22 : 18,
            textDecoration: 'line-through',
            opacity: 0.55,
            fontWeight: 600,
            color: ink
          }}
        >
          {compare}
        </div>
      ) : null}
      <div
        style={{
          fontSize: huge ? 72 : 48,
          fontWeight: 900,
          lineHeight: 1,
          letterSpacing: -1,
          color: ink
        }}
      >
        {main}
      </div>
    </div>
  );
}

function ProductImage({ url, fit, height, radius = 20 }) {
  if (!url) return null;
  return (
    <div
      style={{
        width: '100%',
        height,
        borderRadius: radius,
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.06)'
      }}
    >
      <img
        src={url}
        alt=""
        crossOrigin="anonymous"
        style={{
          width: '100%',
          height: '100%',
          objectFit: fit || 'contain',
          objectPosition: 'center',
          display: 'block'
        }}
      />
    </div>
  );
}

const FlyerCanvas = React.forwardRef(function FlyerCanvas(
  {
    subject,
    brand,
    templateKey = 'impacto',
    formatKey = 'cuadrado',
    title = 'OFERTA',
    subtitle = '',
    cta = 'Consultanos',
    showPrice = true,
    showCompare = true,
    showPromo = true,
    promo = null,
    imageFit = 'contain',
    previewScale
  },
  ref
) {
  const format = getFlyerFormat(formatKey);
  const template = getFlyerTemplate(templateKey);
  const primary = brand?.colors?.primary || '#0F766E';
  const secondary = brand?.colors?.secondary || '#F59E0B';
  const scale =
    previewScale != null
      ? previewScale
      : Math.min(1, 420 / format.width, 560 / format.height);

  const empty = !subject;

  const shell = (children, bg) => (
    <div className="overflow-auto max-w-full" data-testid="flyer-canvas-wrap">
      <div
        style={{
          width: format.width * scale,
          height: format.height * scale,
          overflow: 'hidden'
        }}
      >
        <div
          ref={ref}
          data-testid="flyer-canvas"
          data-template={template.key}
          data-format={format.key}
          style={{
            width: format.width,
            height: format.height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            boxSizing: 'border-box',
            fontFamily: '"Segoe UI", system-ui, sans-serif',
            color: '#f8fafc',
            position: 'relative',
            overflow: 'hidden',
            background: bg
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );

  if (empty) {
    return shell(
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 48,
          textAlign: 'center',
          background: `linear-gradient(160deg, #0b1220, ${primary}55)`
        }}
      >
        <div>
          <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 12 }}>Flyer</div>
          <div style={{ fontSize: 20, opacity: 0.8 }}>Elegí un producto para comenzar.</div>
        </div>
      </div>,
      '#0b1220'
    );
  }

  const img = subject.hasImage ? subject.imageUrl : '';
  const layout = template.layout;

  if (layout === 'impacto') {
    return shell(
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: 48,
          background: `linear-gradient(180deg, #0b1220 0%, ${primary} 120%)`
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <LogoBlock brand={brand} />
          <div
            style={{
              background: secondary,
              color: '#111',
              fontWeight: 900,
              fontSize: 22,
              padding: '10px 18px',
              borderRadius: 12,
              letterSpacing: 1
            }}
          >
            {title || 'OFERTA'}
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', margin: '28px 0' }}>
          <ProductImage url={img} fit={imageFit} height={format.height * 0.42} />
        </div>
        <div style={{ fontSize: 36, fontWeight: 800, lineHeight: 1.15, marginBottom: 16 }}>
          {subject.name}
        </div>
        {subtitle ? (
          <div style={{ fontSize: 20, opacity: 0.8, marginBottom: 16 }}>{subtitle}</div>
        ) : null}
        <PriceBlock
          subject={subject}
          promo={promo}
          showPrice={showPrice}
          showCompare={showCompare}
          showPromo={showPromo}
          accent={secondary}
          huge
        />
        {cta ? (
          <div
            style={{
              marginTop: 24,
              alignSelf: 'flex-start',
              border: `2px solid ${secondary}`,
              padding: '12px 22px',
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 18
            }}
          >
            {cta}
          </div>
        ) : null}
        <ContactFooter brand={brand} />
      </div>,
      primary
    );
  }

  if (layout === 'oferta') {
    return shell(
      <div
        style={{
          height: '100%',
          display: 'grid',
          gridTemplateRows: img ? '38% 1fr' : '1fr',
          background: '#111827'
        }}
      >
        {img ? (
          <div style={{ position: 'relative', background: '#000' }}>
            <ProductImage url={img} fit={imageFit} height="100%" radius={0} />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to bottom, transparent 40%, #111827)'
              }}
            />
            <div style={{ position: 'absolute', top: 28, left: 28 }}>
              <LogoBlock brand={brand} maxH={48} />
            </div>
          </div>
        ) : (
          <div style={{ padding: '36px 40px 0' }}>
            <LogoBlock brand={brand} />
          </div>
        )}
        <div
          style={{
            padding: 40,
            display: 'flex',
            flexDirection: 'column',
            background: `linear-gradient(160deg, #111827, ${primary}cc)`
          }}
        >
          <div
            style={{
              color: secondary,
              fontWeight: 900,
              fontSize: 28,
              letterSpacing: 2,
              marginBottom: 8
            }}
          >
            {title || 'OFERTA'}
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 8 }}>{subject.name}</div>
          {subtitle ? <div style={{ opacity: 0.75, marginBottom: 16 }}>{subtitle}</div> : null}
          <PriceBlock
            subject={subject}
            promo={promo}
            showPrice={showPrice}
            showCompare={showCompare}
            showPromo={showPromo}
            accent={secondary}
            huge
          />
          {cta ? (
            <div
              style={{
                marginTop: 20,
                background: secondary,
                color: '#111',
                fontWeight: 800,
                fontSize: 20,
                textAlign: 'center',
                padding: '14px 18px',
                borderRadius: 14
              }}
            >
              {cta}
            </div>
          ) : null}
          <ContactFooter brand={brand} compact />
        </div>
      </div>,
      '#111827'
    );
  }

  if (layout === 'minimal') {
    return shell(
      <div
        style={{
          height: '100%',
          padding: 56,
          display: 'flex',
          flexDirection: 'column',
          background: '#f4f1ea',
          color: '#1c1917'
        }}
      >
        <LogoBlock brand={brand} ink="#1c1917" />
        <div style={{ height: 24 }} />
        <div
          style={{
            color: primary,
            fontWeight: 800,
            fontSize: 16,
            letterSpacing: 3,
            textTransform: 'uppercase',
            marginBottom: 12
          }}
        >
          {title || 'OFERTA'}
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <ProductImage url={img} fit={imageFit} height={format.height * 0.38} radius={12} />
        </div>
        <div style={{ fontSize: 40, fontWeight: 700, lineHeight: 1.1, marginTop: 24 }}>
          {subject.name}
        </div>
        {subtitle ? (
          <div style={{ fontSize: 18, opacity: 0.65, marginTop: 10 }}>{subtitle}</div>
        ) : null}
        <div style={{ marginTop: 28 }}>
          <PriceBlock
            subject={subject}
            promo={promo}
            showPrice={showPrice}
            showCompare={showCompare}
            showPromo={showPromo}
            accent={secondary}
            huge={false}
            ink={primary}
          />
        </div>
        {cta ? (
          <div style={{ marginTop: 24, fontWeight: 700, fontSize: 18, color: primary }}>{cta} →</div>
        ) : null}
        <div style={{ color: '#444', marginTop: 12 }}>
          <ContactFooter brand={brand} compact />
        </div>
      </div>,
      '#f4f1ea'
    );
  }

  if (layout === 'sin_foto') {
    return shell(
      <div
        style={{
          height: '100%',
          padding: 48,
          display: 'flex',
          flexDirection: 'column',
          background: `radial-gradient(circle at 20% 20%, ${secondary}33, transparent 45%), linear-gradient(145deg, #0b1220 0%, ${primary} 100%)`
        }}
      >
        <LogoBlock brand={brand} />
        <div
          style={{
            marginTop: 48,
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: 4,
            color: secondary,
            textTransform: 'uppercase'
          }}
        >
          {title || 'OFERTA'}
        </div>
        <div
          style={{
            marginTop: 20,
            fontSize: Math.min(64, 28 + Math.max(0, 40 - (subject.name?.length || 0))),
            fontWeight: 900,
            lineHeight: 1.05,
            maxWidth: '95%'
          }}
        >
          {subject.name}
        </div>
        {subtitle ? (
          <div style={{ marginTop: 16, fontSize: 22, opacity: 0.8 }}>{subtitle}</div>
        ) : null}
        <div style={{ marginTop: 40 }}>
          <PriceBlock
            subject={subject}
            promo={promo}
            showPrice={showPrice}
            showCompare={showCompare}
            showPromo={showPromo}
            accent={secondary}
            huge
          />
        </div>
        {cta ? (
          <div
            style={{
              marginTop: 36,
              display: 'inline-flex',
              background: '#fff',
              color: '#0b1220',
              fontWeight: 800,
              fontSize: 20,
              padding: '14px 28px',
              borderRadius: 999,
              alignSelf: 'flex-start'
            }}
          >
            {cta}
          </div>
        ) : null}
        <ContactFooter brand={brand} />
      </div>,
      primary
    );
  }

  // comercial (default balanced)
  return shell(
    <div
      style={{
        height: '100%',
        display: 'grid',
        gridTemplateColumns: img ? '1fr 1fr' : '1fr',
        background: '#0f172a'
      }}
    >
      {img ? (
        <div style={{ padding: 36, display: 'flex', alignItems: 'center', background: '#020617' }}>
          <ProductImage url={img} fit={imageFit} height={format.height * 0.7} radius={24} />
        </div>
      ) : null}
      <div
        style={{
          padding: 40,
          display: 'flex',
          flexDirection: 'column',
          background: `linear-gradient(180deg, ${primary}ee, #0f172a)`
        }}
      >
        <LogoBlock brand={brand} maxH={48} />
        <div
          style={{
            marginTop: 28,
            fontSize: 16,
            fontWeight: 800,
            letterSpacing: 2,
            color: secondary,
            textTransform: 'uppercase'
          }}
        >
          {title || 'OFERTA'}
        </div>
        <div style={{ marginTop: 12, fontSize: 34, fontWeight: 800, lineHeight: 1.15 }}>
          {subject.name}
        </div>
        {subtitle ? (
          <div style={{ marginTop: 10, fontSize: 18, opacity: 0.75 }}>{subtitle}</div>
        ) : null}
        <div style={{ marginTop: 28 }}>
          <PriceBlock
            subject={subject}
            promo={promo}
            showPrice={showPrice}
            showCompare={showCompare}
            showPromo={showPromo}
            accent={secondary}
            huge={false}
          />
        </div>
        {cta ? (
          <div
            style={{
              marginTop: 24,
              background: secondary,
              color: '#111',
              fontWeight: 800,
              textAlign: 'center',
              padding: '12px 16px',
              borderRadius: 12,
              fontSize: 18
            }}
          >
            {cta}
          </div>
        ) : null}
        <ContactFooter brand={brand} compact />
      </div>
    </div>,
    primary
  );
});

export default FlyerCanvas;
